import { DeleteOutlined, UserOutlined } from "@ant-design/icons"
import {
  Alert,
  Button,
  Divider,
  Empty,
  Flex,
  Modal,
  Radio,
  Select,
  Skeleton,
  Tag,
  Typography,
  message
} from "antd"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { neutral } from "../../../../components/widgets/tokens.ts"
import { getEntityName } from "../../../../utils/user/get-entity-name.ts"
import {
  getReportShares,
  updateReportShares
} from "../../api/services.ts"
import {
  editorEntriesToPayload,
  shareSaveErrorMessage,
  type ShareEditorEntry,
  sharesToEditorEntries
} from "../../helpers/reportSharing.ts"
import {
  shareTargetKinds,
  visibilityChoicesFor
} from "../../helpers/reportVisibility.ts"
import type { ReportShareLevel } from "../../types/collab.types.ts"
import type { ReportOwner, ReportVisibility } from "../../types/types.ts"
import { ReportSharePeoplePicker } from "../ReportSharePeoplePicker.tsx"

const { Text } = Typography

interface ReportShareModalProps {
  reportId: string
  owner: ReportOwner
  initialVisibility: ReportVisibility
  onClose: () => void
  onSaved: (visibility: ReportVisibility) => void
}

export const ReportShareModal = ({
  reportId,
  owner,
  initialVisibility,
  onClose,
  onSaved
}: ReportShareModalProps) => {
  const { t } = useTranslation()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [visibility, setVisibility] =
    useState<ReportVisibility>(initialVisibility)
  const [savedVisibility, setSavedVisibility] =
    useState<ReportVisibility>(initialVisibility)
  const [entries, setEntries] = useState<ShareEditorEntry[]>([])
  const [saveError, setSaveError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const response = await getReportShares(reportId)
      if (cancelled) return
      if (response.ok && response.data) {
        setVisibility(response.data.visibility)
        setSavedVisibility(response.data.visibility)
        setEntries(sharesToEditorEntries(response.data.shares))
      }
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [reportId])

  const levelOptions = useMemo(
    () => [
      { label: t("reports.share.levels.view"), value: "view" },
      { label: t("reports.share.levels.edit"), value: "edit" }
    ],
    [t]
  )

  const addEntry = (entry: ShareEditorEntry) => {
    setEntries((prev) => [...prev, entry])
  }

  const changeLevel = (key: string, level: ReportShareLevel) => {
    setEntries((prev) =>
      prev.map((entry) => (entry.key === key ? { ...entry, level } : entry))
    )
  }

  const removeEntry = (key: string) => {
    setEntries((prev) => prev.filter((entry) => entry.key !== key))
  }

  const handleSave = async () => {
    setSaving(true)
    setSaveError(null)
    try {
      const response = await updateReportShares(reportId, {
        visibility,
        shares: editorEntriesToPayload(entries)
      })
      if (response.ok) {
        message.success(t("reports.share.saved"))
        onSaved(visibility)
        onClose()
      } else {
        setSaveError(shareSaveErrorMessage(response) ?? t("reports.share.saveError"))
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open
      centered
      maskClosable={false}
      width={560}
      title={t("reports.share.title")}
      onCancel={onClose}
      footer={
        <Flex justify="flex-end" gap="small">
          <Button onClick={onClose}>{t("reports.cancel")}</Button>
          <Button type="primary" loading={saving} onClick={handleSave}>
            {t("reports.share.save")}
          </Button>
        </Flex>
      }
    >
      {loading ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : (
        <Flex vertical gap="middle">
          {saveError && (
            <Alert
              type="error"
              showIcon
              message={saveError}
              closable
              onClose={() => setSaveError(null)}
            />
          )}

          <div>
            <Text strong>{t("reports.share.visibility")}</Text>
            <div style={{ marginTop: 8 }}>
              <Radio.Group
                value={visibility}
                onChange={(e) =>
                  setVisibility(e.target.value as ReportVisibility)
                }
                optionType="button"
                buttonStyle="solid"
                options={visibilityChoicesFor(savedVisibility).map((value) => ({
                  label: t(`reports.visibility.${value}`),
                  value
                }))}
              />
            </div>
          </div>

          <Divider style={{ margin: 0 }} />

          <div>
            <Text strong>{t("reports.share.people")}</Text>
            <ReportSharePeoplePicker
              reportId={reportId}
              owner={owner}
              entries={entries}
              kinds={shareTargetKinds(visibility)}
              onAdd={addEntry}
              className="mt-2 w-full"
            />
          </div>

          <Flex vertical gap="small">
            <Flex
              align="center"
              justify="space-between"
              gap="small"
              style={{
                padding: "6px 4px",
                borderBottom: `1px solid ${neutral.border}`
              }}
            >
              <Flex align="center" gap="small" className="min-w-0">
                <UserOutlined />
                <Text ellipsis>{getEntityName(owner)}</Text>
              </Flex>
              <Tag>{t("reports.access.owner")}</Tag>
            </Flex>

            {entries.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t("reports.share.emptyPeople")}
              />
            ) : (
              entries.map((entry) => (
                <Flex
                  key={entry.key}
                  align="center"
                  justify="space-between"
                  gap="small"
                  data-testid={`share-entry-${entry.key}`}
                  style={{ padding: "2px 4px" }}
                >
                  <Flex align="center" gap="small" className="min-w-0">
                    <UserOutlined />
                    <Text ellipsis>{entry.name}</Text>
                    {entry.kind === "role" && (
                      <Tag color="blue">{t("reports.share.role")}</Tag>
                    )}
                  </Flex>
                  <Flex align="center" gap="small">
                    <Select<ReportShareLevel>
                      size="small"
                      value={entry.level}
                      options={levelOptions}
                      onChange={(level) => changeLevel(entry.key, level)}
                      style={{ width: 96 }}
                      aria-label={t("reports.share.levelFor", {
                        name: entry.name
                      })}
                    />
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      aria-label={t("reports.share.remove", {
                        name: entry.name
                      })}
                      onClick={() => removeEntry(entry.key)}
                    />
                  </Flex>
                </Flex>
              ))
            )}
          </Flex>
        </Flex>
      )}
    </Modal>
  )
}
